import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export type SlugClaimResult = { ok: true; slug: string } | { ok: false; reason: string };

export const validateSlug = (raw: string): { ok: true; slug: string } | { ok: false; reason: string } => {
  const slug = raw.trim().toLowerCase();
  if (slug.length < 3 || slug.length > 30) {
    return { ok: false, reason: 'Use 3 to 30 characters.' };
  }
  if (slug.includes('--')) {
    return { ok: false, reason: 'Cannot contain consecutive hyphens.' };
  }
  if (!SLUG_REGEX.test(slug)) {
    return { ok: false, reason: 'Use lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen.' };
  }
  return { ok: true, slug };
};

export const claimProfileSlug = async (uid: string, requestedSlug: string): Promise<SlugClaimResult> => {
  const validation = validateSlug(requestedSlug);
  if (!validation.ok) return validation;
  const { slug } = validation;

  const userRef = doc(db, 'users', uid);
  const slugRef = doc(db, 'slugs', slug);

  return runTransaction<SlugClaimResult>(db, async (tx) => {
    const slugSnap = await tx.get(slugRef);
    if (slugSnap.exists()) {
      if (slugSnap.data()?.uid !== uid) {
        return { ok: false, reason: 'That URL is already taken. Try another one.' };
      }
      tx.update(userRef, { slug });
      return { ok: true, slug };
    }
    tx.set(slugRef, { uid, createdAt: serverTimestamp() });
    tx.update(userRef, { slug });
    return { ok: true, slug };
  });
};

export const resolveProfileIdentifier = async (identifier: string): Promise<string> => {
  const trimmed = identifier.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed.toLowerCase();
  if (!SLUG_REGEX.test(lower) || lower.length > 30) return trimmed;
  try {
    const snap = await getDoc(doc(db, 'slugs', lower));
    if (snap.exists()) {
      const uid = snap.data()?.uid;
      if (typeof uid === 'string' && uid.length > 0) return uid;
    }
  } catch {
    // fall through to treating identifier as a uid
  }
  return trimmed;
};

export const buildStarProfilePath = (slugOrUid: string): string => `/star/${slugOrUid}`;
